import {
  CSSProperties,
  forwardRef,
  Fragment,
  KeyboardEvent as ReactKeyboardEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { ESCAPE_KEY, TAB_KEY } from "../../../../core/constants/keyboard_keys";
import { getRootPopup } from "../../../../core/helpers/dropdownHelper";
import Button from "../../Button/Button";
import { getFocusable } from "../../../utils/useFocusTrap";
import { getPopupCssVariables } from "./popupGeometry";
import TimeColumn, { TimeColumnHandle } from "./TimeColumn";
import { clamp } from "./timeValueHelper";
import { PopupLayout, SegmentValues, TimeSegmentKey } from "./types";

import styles from "./CustomTimePicker.module.css";

/** Lets the picker move focus in when the popup is already open. */
export interface TimePickerPopupHandle {
  focusFirstColumn: () => void;
}

interface TimePickerPopupProps {
  id: string;
  ariaLabel: string;
  className?: string;
  /** Focus trap container, provided by useTimePickerPopup. */
  popupRef: MutableRefObject<HTMLDivElement | null>;
  /**
   * Tab reached an edge of the popup: `forward` is a plain Tab, otherwise
   * Shift+Tab. The popup does not trap focus, it hands it on.
   */
  onTabOut: (forward: boolean) => void;
  /** Measured before this component mounts, see useTimePickerPopup. */
  layout: PopupLayout | null;
  /** Move focus onto the first column as soon as the popup is in the DOM. */
  autoFocus?: boolean;
  segments: TimeSegmentKey[];
  unitLabels: Record<TimeSegmentKey, string>;
  optionsPerSegment: number[][];
  segmentValues: SegmentValues;
  itemHeight: number;
  columnWidth: number;
  /** Fallback used while `layout` is not measured yet. */
  visibleItems: number;
  scrollMode: boolean;
  disabled?: boolean;
  withActions: boolean;
  doneLabel: string;
  /** Must be referentially stable, the columns are memoized on it. */
  onSelect: (segmentIndex: number, value: number) => void;
  /** Escape, Done, or confirming an option. Returns focus to the trigger. */
  onClose: () => void;
}

/**
 * The dropdown itself, rendered into the shared popup root so it is never
 * clipped by a modal or a scrolling table.
 *
 * Living outside of the picker in the DOM, it cannot be reached by tabbing, so
 * it behaves like a dialog. The shared `useFocusTrap` moves focus in, keeps Tab
 * cycling between the three column selectors and the action buttons, and hands
 * focus back on close. Left/right arrows jump between columns directly.
 *
 * Geometry arrives as CSS custom properties from `popupGeometry`, the same
 * module the picker measured this popup with.
 */
export const TimePickerPopup = forwardRef<
  TimePickerPopupHandle,
  TimePickerPopupProps
>(function TimePickerPopup(
  {
    id,
    ariaLabel,
    className,
    popupRef,
    onTabOut,
    layout,
    autoFocus,
    segments,
    unitLabels,
    optionsPerSegment,
    segmentValues,
    itemHeight,
    columnWidth,
    visibleItems,
    scrollMode,
    disabled,
    withActions,
    doneLabel,
    onSelect,
    onClose,
  },
  ref,
) {
  const columnRefs = useRef<(TimeColumnHandle | null)[]>([]);

  const resolvedVisibleItems = layout?.visibleItems ?? visibleItems;

  const focusColumn = useCallback(
    (index: number) => {
      const target = clamp(index, 0, segments.length - 1);
      columnRefs.current[target]?.focusActiveOption();
    },
    [segments.length],
  );

  /* One stable setter per column, so a re-render does not detach the handles. */
  const columnRefSetters = useMemo(
    () =>
      segments.map((_, index) => (handle: TimeColumnHandle | null) => {
        columnRefs.current[index] = handle;
      }),
    [segments],
  );

  useImperativeHandle(ref, () => ({ focusFirstColumn: () => focusColumn(0) }), [
    focusColumn,
  ]);

  /*
   * Focus the selected option of the first column. Done here rather than through
   * the trap's own focusFirst so that it targets the option that actually holds
   * the roving tabindex, and so it happens as soon as the popup is in the DOM.
   */
  useEffect(() => {
    if (autoFocus) focusColumn(0);
  }, [autoFocus, focusColumn]);

  /**
   * Tab moves freely between the columns and the actions, but at the edges it
   * must be taken over: the popup is portaled to the end of <body>, so a native
   * Tab out of the last option wraps around to the top of the document - inside
   * a modal that lands on its header close button.
   */
  const handleTab = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const focusable = getFocusable(popupRef.current);
    if (!focusable.length) return;

    const current = focusable.indexOf(document.activeElement as HTMLElement);
    const atStart = current <= 0;
    const atEnd = current === focusable.length - 1;

    if (!e.shiftKey && atEnd) {
      e.preventDefault();
      onTabOut(true);
    } else if (e.shiftKey && atStart) {
      e.preventDefault();
      onTabOut(false);
    }
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === ESCAPE_KEY) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === TAB_KEY) handleTab(e);
  };

  /*
   * No visibility:hidden fallback here on purpose. The layout is measured before
   * this component mounts, and a hidden popup would make every option compute to
   * visibility:hidden, which the focus trap treats as not focusable.
   */
  const style: CSSProperties = {
    top: layout?.top ?? 0,
    left: layout?.left ?? 0,
    width: layout?.width,
    ...getPopupCssVariables({
      itemHeight,
      visibleItems: resolvedVisibleItems,
      columnWidth,
    }),
  };

  return ReactDOM.createPortal(
    <div
      ref={popupRef}
      id={id}
      role="dialog"
      aria-label={ariaLabel}
      className={classNames(styles.popup, className)}
      style={style}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.columns}>
        {segments.map((key, index) => (
          <Fragment key={key}>
            {index > 0 && (
              <span className={styles.separator} aria-hidden="true">
                :
              </span>
            )}
            <TimeColumn
              ref={columnRefSetters[index]}
              label={unitLabels[key]}
              segmentIndex={index}
              options={optionsPerSegment[index]}
              selected={segmentValues[key]}
              itemHeight={itemHeight}
              scrollMode={scrollMode}
              pageSize={resolvedVisibleItems}
              disabled={disabled}
              dataCy={`ctp_column_${key}`}
              onSelect={onSelect}
              onFocusColumn={focusColumn}
              onConfirm={onClose}
            />
          </Fragment>
        ))}
      </div>

      {withActions && (
        <div className={styles.actions}>
          <Button
            variant="primary"
            size="small"
            text={doneLabel}
            dataCy="ctp_done_btn"
            // wrapped so the click event is not passed on as an argument
            onClick={() => onClose()}
          />
        </div>
      )}
    </div>,
    getRootPopup(),
  );
});

export default TimePickerPopup;
